import { useTranslation } from 'react-i18next';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { VehiculeCheckTypeInfosManagement } from '../components/VehiculeCheckTypeInfosManagement';
import {
  useGetVehiculeCheckTypeById,
  useUpdateVehiculeCheckType,
} from '@/hooks/vehicule-check-type.hooks';
import {
  vehiculeCheckTypeSchema,
  TVehiculeCheckTypeSchema,
} from '@/schemas/vehicule-check-type/vehicule-check-type.schema';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { VehiculeCheckTypeCheckPoints } from '../components/VehiculeCheckTypeCheckPoints';
import { CheckPointType } from '@/api/entities/check-point';
import { useGetTab } from '@/hooks/tab.hooks';
import { EditVehiculeCheckTypeTab } from '@/type/ui/tab/vehiculeCheckTypeTab';
import { getLanguage, getTranslatedItem } from '@/utils/language.utils';

export const EditVehiculeCheckTypePage = () => {
  const { id } = useParams();
  const { t } = useTranslation('inspectionType');
  const { data, isLoading } = useGetVehiculeCheckTypeById(`${id}`);
  const navigate = useNavigate();

  const translatedVehiculeCheckType = getTranslatedItem(
    data?.data?.translatedVehiculeCheckTypes,
  );

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditVehiculeCheckTypeTab.INFOS,
  });

  const {
    watch: watchVehiculeCheckType,
    control: vehiculeCheckTypeControl,
    register: registerVehiculeCheckType,
    setValue: setVehiculeCheckTypeValue,
    setError: setVehiculeCheckTypeError,
    handleSubmit: handleSubmitVehiculeCheckType,
    formState: {
      errors: vehiculeCheckTypeErrors,
      isSubmitting: isSubmittingVehiculeCheckType,
    },
  } = useForm<TVehiculeCheckTypeSchema>({
    resolver: zodResolver(vehiculeCheckTypeSchema),
  });

  const {
    mutate: mutateUpdateVehiculeCheckType,
    isPending: isPendingUpdateVehiculeCheckType,
  } = useUpdateVehiculeCheckType({
    t,
    handleSuccess: () => navigate('/vehicule-check-types'),
  });

  const onSubmitVehiculeCheckTypeInfos = (
    vehiculeCheckType: TVehiculeCheckTypeSchema,
  ) => {
    const { languageCode } = getLanguage();

    const dataWithLanguageCode = {
      ...vehiculeCheckType,
      languageCode,
    };

    const vehiculeCheckTypeWithId = { id, ...dataWithLanguageCode };
    mutateUpdateVehiculeCheckType(vehiculeCheckTypeWithId);
  };

  return (
    <>
      <PageHeader
        backToLink="/vehicule-check-types"
        title={t('theInspectionType')}
        subTitle={translatedVehiculeCheckType?.name || data?.data?.name}
        subTitleIconClassName="ki-notepad-bookmark"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('inspectionTypeList')}
              linkTo="/vehicule-check-types"
            />
            <BreadcrumbItem
              text={translatedVehiculeCheckType?.name || data?.data?.name}
              active
            />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('inspectionTypeNotFound')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditVehiculeCheckTypeTab.INFOS}
              onChange={() => navigateToTab(EditVehiculeCheckTypeTab.INFOS)}
            />
            <TabItem
              text={t('common:checkPoints')}
              leftIcon={<i className="ki-outline ki-fingerprint-scanning" />}
              active={currentTab === EditVehiculeCheckTypeTab.CHECK_POINTS}
              onChange={() =>
                navigateToTab(EditVehiculeCheckTypeTab.CHECK_POINTS)
              }
            />
          </TabList>
          {currentTab === EditVehiculeCheckTypeTab.INFOS && (
            <VehiculeCheckTypeInfosManagement<TVehiculeCheckTypeSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchVehiculeCheckType}
              control={vehiculeCheckTypeControl}
              register={registerVehiculeCheckType}
              setValue={setVehiculeCheckTypeValue}
              setError={setVehiculeCheckTypeError}
              errors={vehiculeCheckTypeErrors}
              submitDisabled={
                isSubmittingVehiculeCheckType ||
                isPendingUpdateVehiculeCheckType
              }
              onSubmit={handleSubmitVehiculeCheckType(
                onSubmitVehiculeCheckTypeInfos,
              )}
            />
          )}
          {currentTab === EditVehiculeCheckTypeTab.CHECK_POINTS && (
            <div className="flex flex-col gap-12">
              <VehiculeCheckTypeCheckPoints<TVehiculeCheckTypeSchema>
                t={t}
                title={
                  <div className="flex items-center gap-2">
                    <i className="ki-filled ki-youtube text-purple-500 text-[25px]" />
                    <p>{t('common:checkPointVideos')}</p>
                  </div>
                }
                type={CheckPointType.VIDEO}
                data={!isLoading && data?.data}
                watch={watchVehiculeCheckType}
                control={vehiculeCheckTypeControl}
                register={registerVehiculeCheckType}
                setValue={setVehiculeCheckTypeValue}
                setError={setVehiculeCheckTypeError}
                errors={vehiculeCheckTypeErrors}
                submitDisabled={
                  isSubmittingVehiculeCheckType ||
                  isPendingUpdateVehiculeCheckType
                }
                onSubmit={handleSubmitVehiculeCheckType(
                  onSubmitVehiculeCheckTypeInfos,
                )}
              />
              <VehiculeCheckTypeCheckPoints<TVehiculeCheckTypeSchema>
                t={t}
                title={
                  <div className="flex items-center gap-2">
                    <i className="ki-filled ki-picture text-purple-500 text-[25px]" />
                    <p>{t('common:checkPointPhotos')}</p>
                  </div>
                }
                type={CheckPointType.PHOTO}
                data={!isLoading && data?.data}
                watch={watchVehiculeCheckType}
                control={vehiculeCheckTypeControl}
                register={registerVehiculeCheckType}
                setValue={setVehiculeCheckTypeValue}
                setError={setVehiculeCheckTypeError}
                errors={vehiculeCheckTypeErrors}
                submitDisabled={
                  isSubmittingVehiculeCheckType ||
                  isPendingUpdateVehiculeCheckType
                }
                onSubmit={handleSubmitVehiculeCheckType(
                  onSubmitVehiculeCheckTypeInfos,
                )}
              />
            </div>
          )}
        </div>
      )}
      {(isLoading ||
        isSubmittingVehiculeCheckType ||
        isPendingUpdateVehiculeCheckType) && <Loader />}
    </>
  );
};
